import { store } from '@/app/store';
import { getLocalUserId } from '@/shared/utils';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const EMPTY_ARRAY = [];

export const useGroupParticipants = (groupId, searchText = '') => {
   const localUserId = getLocalUserId();
   const participants = useSelector(state => state.groupParticipantsData.participants[groupId] || EMPTY_ARRAY);
   const totalParticipantsCount = participants.length;
   const userInfo = useSelector(state => state.userInfoData?.users);

   const filteredParticipants = useMemo(() => {
      let result = participants;
      if (searchText) {
         // Normalize search text: convert non-breaking spaces to regular spaces
         const normalizedSearch = searchText.replace(/\u00A0/g, ' ').toLowerCase();

         result = participants.filter(item => {
            const profile = userInfo?.[item.userId] || {};
            const name = profile.nickName || item.userId || '';
            // Normalize name for comparison as well
            return name
               .replace(/\u00A0/g, ' ')
               .toLowerCase()
               .includes(normalizedSearch);
         });
      }

      const getNickName = item => {
         const profile = userInfo?.[item.userId] || {};
         return profile.nickName || item.userId || '';
      };

      // Local user first, then alphabetical by nickname (case-insensitive, locale-aware)
      return result.slice().sort((a, b) => {
         if (a.userId === localUserId) return -1;
         if (b.userId === localUserId) return 1;
         return getNickName(a).localeCompare(getNickName(b), undefined, { sensitivity: 'base' });
      });
   }, [participants, searchText, userInfo, localUserId]);

   return { participants: filteredParticipants, totalParticipantsCount };
};

export const useAmIGroupAdmin = groupId => {
   const currentUserId = getLocalUserId();
   const { participants } = useGroupParticipants(groupId);
   const currentUser = participants.find(p => p.userId === currentUserId);
   return currentUser?.userType === 'o';
};

export const useAmIParticipant = groupId => {
   const currentUserId = getLocalUserId();
   const { participants } = useGroupParticipants(groupId);
   return participants.some(p => p.userId === currentUserId);
};

export const useMyRoleInGroup = groupId => {
   const currentUserId = getLocalUserId();
   const { participants } = useGroupParticipants(groupId);
   const currentUser = participants.find(p => p.userId === currentUserId);
   return currentUser?.userType;
};

export const getAmIParticipant = groupId => {
   const currentUserId = getLocalUserId();
   const participants = store.getState().groupParticipantsData.participants[groupId];
   return participants?.some(p => p.userId === currentUserId);
};
